import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

/* ======================= UŁAMKI — helpery ======================= */
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function simplifyFrac(p, q) {
  if (q < 0) { p = -p; q = -q; }
  const g = gcd(Math.abs(p), Math.abs(q));
  return [p / g, q / g];
}

function fracLatexSigned(p, q) {
  if (q === 1) return `${p}`;
  const neg = (p < 0) !== (q < 0);
  const A = Math.abs(p), B = Math.abs(q);
  return `${neg ? "-" : ""}\\tfrac{${A}}{${B}}`;
}

function bestLatexFromNumDen(p, q) {
  [p, q] = simplifyFrac(p, q);
  if (q === 1) return `${p}`;
  const absP = Math.abs(p), absQ = Math.abs(q);
  const neg = p < 0;
  if (absP > absQ) {
    const whole = Math.floor(absP / absQ);
    const rem = absP % absQ;
    if (rem === 0) return `${neg ? "-" : ""}${whole}`;
    return `${neg ? "-" : ""}${whole}\\,\\tfrac{${rem}}{${absQ}}`;
  }
  return `${neg ? "-" : ""}\\tfrac{${absP}}{${absQ}}`;
}

/* ======================= GENEROWANIE ZADANIA ======================= */
export function generateTask() {
  let a, b, c, d, x;

  // a ≠ c
  do {
    a = Math.floor(Math.random() * 9 + 1) * (Math.random() < 0.5 ? -1 : 1);
    c = Math.floor(Math.random() * 9 + 1) * (Math.random() < 0.5 ? -1 : 1);
  } while (a === c);

  // b, d ≠ 0 (żeby było „żywsze”)
  do { b = Math.floor(Math.random() * 11 - 5); } while (b === 0);
  do { d = Math.floor(Math.random() * 11 - 5); } while (d === 0);

  const numerator   = d - b;
  const denominator = a - c;
  x = numerator / denominator;

  // odrzuć skrajne/trywialne przypadki
  if (Math.abs(x) > 10 || Number.isInteger(x)) {
    return generateTask();
  }

  const aStr = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bStr = b > 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const cStr = c === 1 ? "x" : c === -1 ? "-x" : `${c}x`;
  const dStr = d > 0 ? `+ ${d}` : `- ${Math.abs(d)}`;

  const expression = `${aStr} ${bStr} = ${cStr} ${dStr}`.replace(/\s+/g, " ").trim();

  return {
    type: "SolveTwoSidedLinearEquationTask",
    data: { a, b, c, d, x, expression },
    answer: x
  };
}

/* ======================= RENDER TASK ======================= */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż równanie: <BlockMath math={data.expression} />
    </p>
  );
}

/* ======================= INPUT ======================= */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math="x = " /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="x"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = userAnswer?.parsed;
  const expected = data.x;
  const actual = parsed ?? NaN;
  const isCorrect = parsed != null && !isNaN(parsed) && Math.abs(parsed - expected) < 0.01;
  return { isCorrect, expected, actual };
}

/* ======================= RENDER ANSWER ======================= */
/* Odpowiedź tak jak w wyjaśnieniu: z licznika i mianownika, bez zapisu dziesiętnego */
export function renderAnswer({ data }) {
  const numerator   = data.d - data.b;
  const denominator = data.a - data.c;
  const latex = bestLatexFromNumDen(numerator, denominator);
  return <InlineMath math={`x = ${latex}`} />;
}

/* ======================= WYJAŚNIENIE ======================= */
export function renderExplanation({ data }) {
  const { a, b, c, d } = data;

  // po przeniesieniach
  const numerator   = d - b;   // d − b
  const denominator = a - c;   // a − c

  // składnia do równań
  const bx   = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const dx   = d >= 0 ? `+ ${d}` : `- ${Math.abs(d)}`;
  const opCx = c >= 0 ? `- ${c}x` : `+ ${Math.abs(c)}x`;   // Krok 1
  const opB  = b >= 0 ? `- ${b}` : `+ ${Math.abs(b)}`;     // Krok 2
  const denPar = denominator < 0 ? `(${denominator})` : `${denominator}`;

  const verbCx = c >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const verbB  = b >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";

  // Krok 3: najpierw „surowy” ułamek, potem wersja po skróceniu/mieszana (jeśli inna)
  const rawFrac  = fracLatexSigned(numerator, denominator);
  const bestFrac = bestLatexFromNumDen(numerator, denominator);
  const showSecond = rawFrac !== bestFrac;

  return (
    <div className="space-y-2" style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1:</strong> {verbCx} {Math.abs(c)}<InlineMath math="x" />.</p>
      <BlockMath math={`${a}x ${bx} = ${c}x ${dx}\\;\\Big|\\ ${opCx}`} />
      <BlockMath math={`${a}x ${bx} ${opCx} = ${d}`} />
      <BlockMath math={`${a - c}x ${bx} = ${d}`} />

      <p><strong>Krok 2:</strong> {verbB} {Math.abs(b)}.</p>
      <BlockMath math={`${a - c}x ${bx} = ${d}\\;\\Big|\\ ${opB}`} />
      <BlockMath math={`${a - c}x = ${d} ${b >= 0 ? "- " + b : "+ " + Math.abs(b)}`} />
      <BlockMath math={`${a - c}x = ${numerator}`} />

      <p><strong>Krok 3:</strong> Podziel obie strony przez <InlineMath math={denPar} />.</p>
      <BlockMath math={`${a - c}x = ${numerator}\\;\\Big|\\ :\\ ${denPar}`} />
      {/* najpierw surowy ułamek */}
      <BlockMath math={`x = ${rawFrac}`} />
      {/* a poniżej wersja po skróceniu / mieszana, tylko jeśli różna */}
      {showSecond && <BlockMath math={`x = ${bestFrac}`} />}
    </div>
  );
}

/* ======================= RENDER USER ANSWER ======================= */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw, "x");
  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
